import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import { useCart } from '../../context/CartContext';
import { generateInvoice } from '../../utils/generateInvoice';

const BRAND_BLUE = '#0B3D6E';
const BRAND_TEAL = '#0F6E56';

function DownloadBtn({ order, lang, type = 'facture' }) {
  const [loading, setLoading] = useState(false);
  const label = type === 'devis' ? 'Devis' : 'Facture';
  const handle = async () => {
    setLoading(true);
    try { await generateInvoice(order, lang, type); toast.success(`${label} téléchargé !`); }
    catch(e) { toast.error('Erreur PDF'); }
    finally { setLoading(false); }
  };
  return (
    <button onClick={handle} disabled={loading} style={{
      flex:1, display:'flex', alignItems:'center', justifyContent:'center', gap:8,
      background: type==='devis' ? BRAND_TEAL : BRAND_BLUE,
      color:'#fff', border:'none', padding:'11px', borderRadius:8, fontSize:13, fontWeight:600, cursor:'pointer', opacity:loading?0.6:1
    }}>
      {loading ? 'Génération...' : `⬇ ${label} PDF`}
    </button>
  );
}

export default function Reservation({ lang }) {
  const { items, changeQty, clearCart } = useCart();
  const fr = lang === 'fr';
  const [confirmation, setConfirmation] = useState(null);
  const [form, setForm] = useState({
    institution:'', institutionType:'', city:'Casablanca',
    contactName:'', phone:'', email:'',
    freeText:'', deliveryDate:'', paymentMethod:'Bon de commande (BC)', notes:''
  });

  const mutation = useMutation({
    mutationFn: data => api.post('/orders', data),
    onSuccess: res => {
      setConfirmation(res.data.data);
      clearCart();
      setForm({ institution:'', institutionType:'', city:'Casablanca', contactName:'', phone:'', email:'', freeText:'', deliveryDate:'', paymentMethod:'Bon de commande (BC)', notes:'' });
    },
    onError: err => toast.error(err.response?.data?.message || "Erreur lors de l'envoi")
  });

  const handleSubmit = () => {
    if (!form.institution || !form.contactName || !form.phone) {
      toast.error(fr?'Veuillez remplir les champs obligatoires':'Please fill required fields');
      return;
    }
    const cartItems = items.map(i => ({ productId:i._id, productRef:i.ref, productName:i.nameFr, quantity:i.qty, unitPrice:i.price }));
    mutation.mutate({ ...form, items: cartItems });
  };

  const cities = ['Casablanca','Rabat','Marrakech','Fès','Tanger','Agadir','Meknès','Oujda','Autre'];
  const types  = fr
    ? ['Choisir...','Hôpital public','Clinique privée','Polyclinique','Centre de santé','Pharmacie hospitalière','Laboratoire d\'analyses']
    : ['Select...','Public hospital','Private clinic','Polyclinic','Health center','Hospital pharmacy','Analysis laboratory'];
  const pays   = fr
    ? ['Bon de commande (BC)','Virement bancaire','Chèque certifié','Paiement à la livraison']
    : ['Purchase order (PO)','Bank transfer','Certified cheque','Cash on delivery'];

  const inp = { width:'100%', border:'0.5px solid #e2e8f0', borderRadius:8, padding:'10px 12px', fontSize:13, background:'#f8fafc', color:'#1a2535', outline:'none', fontFamily:'inherit', boxSizing:'border-box' };
  const lbl = { display:'block', fontSize:11, fontWeight:500, color:'#64748b', textTransform:'uppercase', letterSpacing:'0.5px', marginBottom:5 };

  if (confirmation) {
    return (
      <div style={{ maxWidth:560, margin:'0 auto', padding:'1rem' }}>
        <div style={{ background:'#fff', border:'0.5px solid #e2e8f0', borderRadius:16, overflow:'hidden', boxShadow:'0 4px 20px rgba(0,0,0,0.08)' }}>
          <div style={{ background:`linear-gradient(135deg,${BRAND_BLUE},${BRAND_TEAL})`, padding:'2rem', textAlign:'center' }}>
            <div style={{ width:60, height:60, background:'rgba(255,255,255,0.2)', borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 14px' }}>
              <svg viewBox="0 0 24 24" style={{ width:28, height:28, fill:'none', stroke:'#fff', strokeWidth:2.5 }}><path d="M5 13l4 4L19 7"/></svg>
            </div>
            <h1 style={{ fontSize:22, fontWeight:600, color:'#fff', marginBottom:6 }}>{fr?'Réservation confirmée !':'Reservation confirmed!'}</h1>
            <p style={{ fontSize:13, color:'rgba(255,255,255,0.85)' }}>{fr?'Votre demande a bien été enregistrée.':'Your request has been successfully recorded.'}</p>
          </div>

          <div style={{ padding:'1.5rem', background:'#f8fafc', borderBottom:'0.5px solid #e2e8f0', textAlign:'center' }}>
            <div style={{ fontSize:11, fontWeight:500, color:'#64748b', textTransform:'uppercase', letterSpacing:'1px', marginBottom:8 }}>{fr?'Numéro de commande':'Order number'}</div>
            <div style={{ fontSize:28, fontWeight:600, color:BRAND_BLUE, fontFamily:'monospace', marginBottom:6 }}>{confirmation.orderNumber}</div>
            <p style={{ fontSize:12, color:'#94a3b8' }}>{fr?'Conservez ce numéro pour suivre votre commande':'Keep this number to track your order'}</p>
          </div>

          <div style={{ padding:'1.5rem' }}>
            {[
              [fr?'Établissement':'Institution', confirmation.institution],
              [fr?'Responsable':'Contact', confirmation.contactName],
              [fr?'Ville':'City', confirmation.city],
              [fr?'Mode paiement':'Payment', confirmation.paymentMethod],
              [fr?'Date demande':'Request date', new Date(confirmation.createdAt).toLocaleDateString(fr?'fr-FR':'en-GB',{day:'2-digit',month:'long',year:'numeric'})],
            ].map(([l,v]) => (
              <div key={l} style={{ display:'flex', justifyContent:'space-between', padding:'7px 0', borderBottom:'0.5px solid #f1f5f9' }}>
                <span style={{ fontSize:12, color:'#94a3b8' }}>{l}</span>
                <span style={{ fontSize:13, fontWeight:500 }}>{v}</span>
              </div>
            ))}

            <div style={{ background:'#fef3c7', border:'0.5px solid #fcd34d', borderRadius:10, padding:'12px', display:'flex', gap:10, margin:'1rem 0' }}>
              <svg viewBox="0 0 24 24" style={{ width:18, height:18, fill:'none', stroke:'#d97706', strokeWidth:2, flexShrink:0, marginTop:1 }}><circle cx="12" cy="12" r="10"/><path d="M12 8v4l3 3"/></svg>
              <div>
                <div style={{ fontSize:13, fontWeight:600, color:'#d97706', marginBottom:2 }}>{fr?'En attente de traitement':'Pending review'}</div>
                <div style={{ fontSize:12, color:'#92400e', lineHeight:1.6 }}>{fr?'Notre équipe vous contactera sous 24h.':'Our team will contact you within 24h.'}</div>
              </div>
            </div>

            {/* PDF Buttons */}
            <div style={{ display:'flex', gap:10, marginBottom:10 }}>
              <DownloadBtn order={confirmation} lang={lang} type="devis"/>
              <DownloadBtn order={confirmation} lang={lang} type="facture"/>
            </div>

            <div style={{ display:'flex', gap:10 }}>
              <button onClick={()=>setConfirmation(null)} style={{ flex:1, border:'0.5px solid #e2e8f0', background:'#fff', color:'#64748b', padding:'10px', borderRadius:8, fontSize:13, cursor:'pointer' }}>
                {fr?'Nouvelle réservation':'New reservation'}
              </button>
              <Link to="/mes-commandes" style={{ flex:1, background:'#1a2535', color:'#fff', padding:'10px', borderRadius:8, fontSize:13, fontWeight:600, textAlign:'center', textDecoration:'none', display:'flex', alignItems:'center', justifyContent:'center', gap:6 }}>
                {fr?'Suivre la commande':'Track order'}
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth:960, margin:'0 auto', padding:'1rem' }}>
      <div style={{ fontSize:11, fontWeight:500, textTransform:'uppercase', letterSpacing:'1px', color:BRAND_TEAL, marginBottom:4 }}>{fr?'Commande en ligne':'Online order'}</div>
      <h1 style={{ fontSize:20, fontWeight:600, marginBottom:6 }}>{fr?'Réservation — LOMED MEDICARE':'Reservation — LOMED MEDICARE'}</h1>
      <p style={{ fontSize:13, color:'#64748b', marginBottom:'1.5rem' }}>{fr?'Formulaire réservé aux établissements de santé agréés':'Form reserved for licensed healthcare institutions'}</p>

      <div style={{ display:'grid', gap:16 }} className="res-layout">
        <style>{`.res-layout{grid-template-columns:minmax(0,1fr) 280px}@media(max-width:700px){.res-layout{grid-template-columns:1fr !important}}`}</style>

        {/* Formulaire */}
        <div style={{ background:'#fff', border:'0.5px solid #e2e8f0', borderRadius:12, padding:'1.5rem', boxShadow:'0 1px 3px rgba(0,0,0,0.06)' }}>
          <h2 style={{ fontSize:15, fontWeight:600, marginBottom:14, paddingBottom:12, borderBottom:'0.5px solid #f1f5f9' }}>
            {fr?"Informations de l'établissement":'Institution information'}
          </h2>
          <div style={{ display:'grid', gap:14 }} className="fgrid">
            <style>{`.fgrid{grid-template-columns:1fr 1fr}@media(max-width:500px){.fgrid{grid-template-columns:1fr !important}}`}</style>
            <div style={{ gridColumn:'1/-1' }}>
              <label style={lbl}>{fr?"Nom de l'établissement *":'Institution name *'}</label>
              <input style={inp} value={form.institution} onChange={e=>setForm({...form,institution:e.target.value})} placeholder={fr?'Ex : CHU Ibn Rochd...':'E.g.: Ibn Rochd Hospital...'}/>
            </div>
            <div>
              <label style={lbl}>{fr?'Type *':'Type *'}</label>
              <select style={inp} value={form.institutionType} onChange={e=>setForm({...form,institutionType:e.target.value})}>
                {types.map(t=><option key={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label style={lbl}>{fr?'Ville *':'City *'}</label>
              <select style={inp} value={form.city} onChange={e=>setForm({...form,city:e.target.value})}>
                {cities.map(c=><option key={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label style={lbl}>{fr?'Responsable achats *':'Purchasing manager *'}</label>
              <input style={inp} value={form.contactName} onChange={e=>setForm({...form,contactName:e.target.value})}/>
            </div>
            <div>
              <label style={lbl}>{fr?'Téléphone *':'Phone *'}</label>
              <input style={inp} value={form.phone} onChange={e=>setForm({...form,phone:e.target.value})} placeholder="+212 6XX XXX XXX"/>
            </div>
            <div style={{ gridColumn:'1/-1' }}>
              <label style={lbl}>Email</label>
              <input type="email" style={inp} value={form.email} onChange={e=>setForm({...form,email:e.target.value})}/>
            </div>
            <div style={{ gridColumn:'1/-1' }}>
              <label style={lbl}>{fr?'Produits & quantités':'Products & quantities'}</label>
              <textarea style={{ ...inp, minHeight:80, resize:'vertical' }} value={form.freeText} onChange={e=>setForm({...form,freeText:e.target.value})} placeholder={fr?'Ex: Tensiomètre numérique — 5 unités\nStéthoscope — 10 unités':'E.g.: Blood pressure monitor — 5 units\nStethoscope — 10 units'}/>
            </div>
            <div>
              <label style={lbl}>{fr?'Date de livraison':'Delivery date'}</label>
              <input type="date" style={inp} value={form.deliveryDate} onChange={e=>setForm({...form,deliveryDate:e.target.value})}/>
            </div>
            <div>
              <label style={lbl}>{fr?'Mode de paiement':'Payment method'}</label>
              <select style={inp} value={form.paymentMethod} onChange={e=>setForm({...form,paymentMethod:e.target.value})}>
                {pays.map(p=><option key={p}>{p}</option>)}
              </select>
            </div>
            <div style={{ gridColumn:'1/-1' }}>
              <label style={lbl}>{fr?'Remarques':'Notes'}</label>
              <textarea style={{ ...inp, minHeight:55, resize:'vertical' }} value={form.notes} onChange={e=>setForm({...form,notes:e.target.value})}/>
            </div>
          </div>
          <button onClick={handleSubmit} disabled={mutation.isPending} style={{ width:'100%', marginTop:16, background:BRAND_BLUE, color:'#fff', border:'none', padding:'12px', borderRadius:8, fontSize:14, fontWeight:600, cursor:'pointer', opacity:mutation.isPending?0.6:1 }}>
            {mutation.isPending?(fr?'Envoi en cours...':'Sending...'):(fr?'Envoyer la réservation à LOMED MEDICARE':'Submit reservation to LOMED MEDICARE')}
          </button>
        </div>

        {/* Panier */}
        <div>
          <div style={{ background:'#fff', border:'0.5px solid #e2e8f0', borderRadius:12, padding:'1rem', boxShadow:'0 1px 3px rgba(0,0,0,0.06)', marginBottom:12 }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
              <span style={{ fontWeight:600, fontSize:13 }}>{fr?'Récapitulatif':'Summary'}</span>
              <button onClick={clearCart} style={{ fontSize:11, color:'#94a3b8', border:'0.5px solid #e2e8f0', background:'#f8fafc', padding:'3px 8px', borderRadius:6, cursor:'pointer' }}>{fr?'Vider':'Clear'}</button>
            </div>
            {items.length===0 ? (
              <p style={{ fontSize:12, color:'#94a3b8', textAlign:'center', padding:'1.5rem 0', lineHeight:1.7 }}>
                {fr?'Ajoutez des produits depuis le catalogue.':'Add products from the catalogue.'}
              </p>
            ) : (
              <>
                {items.map(i => (
                  <div key={i._id} style={{ display:'flex', alignItems:'center', gap:8, padding:'7px 0', borderBottom:'0.5px solid #f8fafc', fontSize:12 }}>
                    <span style={{ flex:1, fontWeight:500 }}>{lang==='fr'?i.nameFr:i.nameEn}</span>
                    <div style={{ display:'flex', alignItems:'center', gap:4 }}>
                      <button onClick={()=>changeQty(i._id,-1)} style={{ width:20, height:20, border:'0.5px solid #e2e8f0', borderRadius:5, background:'#f1f5f9', cursor:'pointer', fontSize:13 }}>−</button>
                      <span style={{ minWidth:16, textAlign:'center' }}>{i.qty}</span>
                      <button onClick={()=>changeQty(i._id,1)} style={{ width:20, height:20, border:'0.5px solid #e2e8f0', borderRadius:5, background:'#f1f5f9', cursor:'pointer', fontSize:13 }}>+</button>
                    </div>
                    <span style={{ color:BRAND_BLUE, fontWeight:600, minWidth:55, textAlign:'right' }}>{i.price}</span>
                  </div>
                ))}
                <div style={{ display:'flex', justifyContent:'space-between', paddingTop:10, fontWeight:600, fontSize:13 }}>
                  <span>{items.length} {fr?'réf.':'item(s)'}</span>
                  <span style={{ color:BRAND_TEAL }}>{fr?'Devis sur demande':'Quote on request'}</span>
                </div>
              </>
            )}
          </div>
          <Link to="/mes-commandes" style={{ display:'flex', alignItems:'center', gap:8, background:'#E1F5EE', border:'0.5px solid #9FE1CB', borderRadius:10, padding:'10px 12px', textDecoration:'none', fontSize:12, color:BRAND_TEAL, fontWeight:600, marginBottom:10 }}>
            <svg viewBox="0 0 24 24" style={{ width:14, height:14, fill:'none', stroke:BRAND_TEAL, strokeWidth:2 }}><path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2"/><path d="M9 12h6M9 16h4"/></svg>
            {fr?'Suivre une commande existante':'Track an existing order'}
          </Link>
          <div style={{ background:'#f8fafc', border:'0.5px solid #e2e8f0', borderRadius:10, padding:'10px 12px', fontSize:12, color:'#64748b', lineHeight:1.8 }}>
            <strong style={{ color:'#1a2535', display:'block', marginBottom:3 }}>LOMED MEDICARE</strong>
            Casablanca, Maroc<br/>contact@lomedmedicare.ma<br/>+212 5XX XX XX XX
          </div>
        </div>
      </div>
    </div>
  );
}

